import { useState } from "react";
import { KacBadge } from "./KacMark";
import { login, requestAccount } from "../utils/auth";

// Sign in, or ask for an account. Both go to the server — there is no
// password in this bundle any more. A requested account can do nothing until
// an admin activates it, which is what the confirmation screen says.
export default function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState("signin"); // "signin" | "request"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);

  function switchMode(next) {
    setMode(next);
    setError(null);
    setPassword("");
    setConfirm("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (mode === "request" && password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }

    setBusy(true);
    try {
      if (mode === "signin") {
        const user = await login(email.trim(), password);
        onLogin(user);
      } else {
        await requestAccount({ name: name.trim(), email: email.trim(), password });
        setRequested(true);
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (requested) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="max-w-sm w-full text-center">
          <div className="flex justify-center">
            <KacBadge className="mb-4" size="w-16 h-16 text-3xl rounded-2xl" />
          </div>
          <h1 className="text-2xl font-display text-brand-blue">Request sent</h1>
          <p className="text-gray-600 text-sm mt-3">
            An admin has to activate <span className="font-medium break-all">{email.trim()}</span>{" "}
            before you can sign in. The password you just chose will work once they do.
          </p>
          <button
            onClick={() => {
              setRequested(false);
              switchMode("signin");
            }}
            className="mt-6 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <KacBadge className="mb-4" size="w-16 h-16 text-3xl rounded-2xl" />
          <h1 className="text-3xl font-display text-brand-blue">Kings Athletic Club</h1>
          <p className="text-gray-500 text-sm mt-1">
            {mode === "signin" ? "Sign in to access the schedule" : "Ask an admin for access"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          {mode === "request" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
              <input
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setError(null); }}
                placeholder="Jane Doe"
                required
                autoComplete="name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(null); }}
              placeholder="you@example.com"
              required
              autoComplete="username"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(null); }}
                placeholder="••••••••"
                required
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {mode === "request" && (
              <p className="text-xs text-gray-400 mt-1">At least 8 characters.</p>
            )}
          </div>

          {mode === "request" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirm}
                onChange={e => { setConfirm(e.target.value); setError(null); }}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2 rounded-lg text-sm transition-colors"
          >
            {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Request an account"}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          {mode === "signin" ? (
            <>
              Work here and need access?{" "}
              <button
                type="button"
                onClick={() => switchMode("request")}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Request an account
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => switchMode("signin")}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Sign in
              </button>
            </>
          )}
        </p>

        <p className="text-center text-sm text-gray-500 mt-3">
          Joining the club as a member?{" "}
          <a href="/?join" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign up here
          </a>
        </p>
      </div>
    </div>
  );
}
